import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ProductCell } from './components/ProductCell'
import { Table } from './components/Table'
import { SquareAmountCell } from './components/SquareAmountCell'
import { PickingCheckboxCell } from './components/PickingCheckboxCell'
import { EditExpeditionBatchPositionModal } from '../../components'
import { graphql, useFragment } from '../../gql'
import { ClearanceItemsInTable_OrderFragmentFragment } from '../../gql/graphql'
import { useOrderDetailContext } from './OrderDetailContext'
import { formatDate } from '../../lib/Util'
import { FragmentValue } from '../../lib/RequestUtils'

const ClearanceItemsInTable_OrderFragment = graphql(`
  fragment ClearanceItemsInTable_OrderFragment on Order {
    clearanceItems {
      id
      position
      amount
      product {
        ...ProductCell_ProductFragment
        ean
        ean2
        ean3
        ean4
        ean5
        code
      }
      batch
      expirationDate
      picked
      position
      ...EditExpeditionBatchPositionModal_ClearanceItemFragment
    }
    processStatus {
      id
    }
  }
`)

const columnHelper =
  createColumnHelper<FragmentValue<typeof ClearanceItemsInTable_OrderFragment>['clearanceItems'][number]>()

export const ClearanceItems = () => {
  const context = useOrderDetailContext()
  const order = useFragment(ClearanceItemsInTable_OrderFragment, context.order)
  const clearanceItems = order.clearanceItems

  const Columns = useMemo(
    () => [
      columnHelper.accessor('product', {
        header: 'Zboží',
        cell: ({ getValue }) => <ProductCell product={getValue()} />,
      }),
      columnHelper.accessor('product.code', { header: 'Kód', size: 250 }),
      columnHelper.display({
        id: 'product.ean',
        header: 'EAN',
        cell: ({ row }) => {
          const product = row.original.product
          const eans = [
            product?.ean && { label: 'EAN', value: product.ean },
            product?.ean2 && { label: 'EAN2', value: product.ean2 },
            product?.ean3 && { label: 'EAN3', value: product.ean3 },
            product?.ean4 && { label: 'EAN4', value: product.ean4 },
            product?.ean5 && { label: 'EAN5', value: product.ean5 },
          ].filter(Boolean) as Array<{ label: string; value: string }>

          if (eans.length === 0) return null

          // If only one EAN, show just the value
          if (eans.length === 1) {
            return <div className="text-sm">{eans[0].value}</div>
          }

          // If multiple EANs, show with labels
          return (
            <div className="flex flex-col gap-1">
              {eans.map((ean, index) => (
                <div key={index} className="text-sm">
                  {ean.label}: {ean.value}
                </div>
              ))}
            </div>
          )
        },
        size: 120,
      }),
      columnHelper.accessor(({ expirationDate }) => formatDate(expirationDate || new Date()), {
        header: 'Expirace',
        id: 'expirationDate',
        size: 120,
      }),
      columnHelper.accessor('batch', { header: 'Šarže', size: 120 }),
      columnHelper.accessor('position', { header: 'Pozice', size: 120 }),
      columnHelper.accessor('amount', {
        header: () => <div className={'text-center'}>Počet ks</div>,
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 100,
      }),
      columnHelper.accessor('picked', {
        header: () => (
          <div className={'text-center flex-1'}>
            {order.processStatus?.id === 'picking' ? 'Vyskladněno' : 'Zabaleno'}
          </div>
        ),
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 100,
      }),
      columnHelper.accessor('id', {
        header: () => (
          <div className={'text-center flex-1'}>
            {order.processStatus?.id === 'picking' ? 'Vybrat vše' : 'Zabalit vše'}
          </div>
        ),
        cell: ({ getValue }) => <PickingCheckboxCell clearanceItemId={getValue()} />,
        size: 100,
      }),
      columnHelper.display({
        id: 'actions',
        cell: props => <EditExpeditionBatchPositionModal clearanceItem={props.row.original} />,
        size: 100,
      }),
    ],
    [order.processStatus?.id],
  )

  const columnVisibility = useMemo(
    () => ({
      id: order.processStatus?.id !== 'recieved',
      batch: !clearanceItems.every(el => !el.batch),
      expirationDate: !clearanceItems.every(el => !el.expirationDate),
      'product.ean': !clearanceItems.every(
        el => !el.product.ean && !el.product.ean2 && !el.product.ean3 && !el.product.ean4 && !el.product.ean5,
      ),
      'product.code': !clearanceItems.every(el => !el.product.code),
      position: !clearanceItems.every(el => el.position == '--'),
    }),
    [order.processStatus?.id, clearanceItems],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns: Columns,
    data: clearanceItems,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
    defaultColumn: {
      // @ts-ignore
      size: '1fr',
    },
  })

  return <Table table={table} />
}
